import { RefObject, useEffect, useState } from 'react';

interface UseAlignedStripPctArgs {
  /**
   * Off when 7.0 is off, and off when the agent has a saved height — their drag
   * is a preference and outranks the alignment, the same way `restoreOnMount`
   * lets a saved split beat `defaultSize`.
   */
  enabled: boolean;
  /** The stack the percentage is measured against. */
  stackRef: RefObject<HTMLElement>;
  /** The shared bottom-section height in px (HITL7_BOTTOM_SECTION_HEIGHT). */
  targetHeight: number | null;
}

/**
 * HITL-1665 — the Copilot strip's resting height as a PERCENTAGE of its stack,
 * so its top rule lands on the same line as the typing box and the footer.
 *
 * The conversion exists because the two sides speak different units and neither
 * can be changed: the shared floor is a pixel value, set by the action row's
 * own content, while react-resizable-panels v1 sizes panels only in percent. So
 * the stack is measured and the ratio is what the panel can actually be given.
 *
 * Returns null whenever it cannot produce an honest number — 7.0 off, a saved
 * height present, nothing measured yet, or a stack of zero height (which is what
 * a hidden or not-yet-laid-out pane reports). The caller then leaves the panel
 * on its existing default rather than applying a percentage derived from a
 * divide-by-zero.
 */
const useAlignedStripPct = ({
  enabled,
  stackRef,
  targetHeight,
}: UseAlignedStripPctArgs): number | null => {
  const [pct, setPct] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled || !targetHeight) {
      setPct(null);
      return undefined;
    }
    const measure = () => {
      const stackHeight = stackRef.current?.getBoundingClientRect().height ?? 0;
      if (stackHeight <= 0) {
        setPct(null);
        return;
      }
      // One decimal: the panel library rounds layouts anyway, and a full-
      // precision float would re-render the stack on every pixel of pane drag.
      const next = Math.round((targetHeight / stackHeight) * 1000) / 10;
      setPct(next > 0 && next < 100 ? next : null);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    // The stack's own height changes when the agent drags the horizontal pane
    // divider, and the percentage has to follow or the alignment drifts.
    const observer = new ResizeObserver(measure);
    if (stackRef.current) observer.observe(stackRef.current);
    return () => observer.disconnect();
  }, [enabled, stackRef, targetHeight]);

  return pct;
};

export default useAlignedStripPct;
